import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { documentSigningApi, extractOne } from '../../api/facades';
import Button from '../../components/common/Button';
import { DocumentPreview } from '../../components/DocumentSigning/DocumentPreview';
import { SignaturePad } from '../../components/DocumentSigning/SignaturePad';
import { SignatureAuditList } from '../../components/DocumentSigning/SignatureAuditList';
import { axiosInstance } from '../../api/axios-instance';

const DocumentSigningPage: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const numId = documentId ? Number(documentId) : NaN;

  const {
    data: ctxRes,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['document-signing-context', numId],
    queryFn: () => documentSigningApi.getSigningContext(numId),
    enabled: !Number.isNaN(numId),
  });

  const ctx = extractOne(ctxRes);

  const submitMu = useMutation({
    mutationFn: (signatureData: string) =>
      documentSigningApi.submitSignature(numId, {
        signature_data: signatureData,
        status: 'signed',
      }),
    onSuccess: () => {
      toast.success('Signature saved successfully.');
      queryClient.invalidateQueries({ queryKey: ['document-signing-context', numId] });
    },
    onError: (e: any) => {
      const msg = e?.body?.message || e?.message || 'Failed to save signature.';
      toast.error(msg);
    },
  });

  const verifyMu = useMutation({
    mutationFn: (signatureId: number) => documentSigningApi.getSignature(numId, signatureId),
    onSuccess: (res) => {
      const data = extractOne(res as any);
      const signerPeople = data?.signer?.people;
      const name =
        signerPeople && (signerPeople.first_name || signerPeople.last_name)
          ? `${signerPeople.first_name ?? ''} ${signerPeople.last_name ?? ''}`.trim()
          : data?.signer?.email ?? 'Unknown';
      const signedAt = data?.signed_at ? new Date(data.signed_at).toLocaleString() : '—';
      const ip = data?.ip_address || '—';
      toast(
        `Signature by ${name} on ${signedAt} (IP: ${ip})`,
        {
          description: `Status: ${data?.status || 'signed'}`,
        } as any,
      );
    },
    onError: (e: any) => {
      const msg = e?.body?.message || e?.message || 'Failed to verify signature.';
      toast.error(msg);
    },
  });

  const handleDownloadSignedPdf = async () => {
    try {
      const url = `/documents/${numId}/signed-pdf`;
      const res = await axiosInstance.get(url, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${ctx?.document_name || 'signed-document'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 501) {
        toast.info('Signed PDF is not available for this document.');
        return;
      }
      toast.error(e?.message || 'Failed to download signed PDF.');
    }
  };

  if (Number.isNaN(numId) || isError) {
    const msg =
      (error as any)?.response?.status === 403
        ? "You don't have access to this document."
        : 'Document not found.';
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <p className="text-slate-600 dark:text-slate-400">{msg}</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>
    );
  }

  const doc = ctx;
  const signatures = (doc?.signatures as any[]) || [];

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
            {doc?.document_name || 'Sign document'}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Review the document, then draw your signature to sign electronically.
          </p>
        </div>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>

      <DocumentPreview
        documentName={doc?.document_name || 'Document'}
        fileUrl={doc?.file_url ?? undefined}
        height={420}
        className="w-full"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4">
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">Document details</h2>
            {isLoading ? (
              <p className="text-xs text-slate-500">Loading…</p>
            ) : (
              <dl className="space-y-1 text-xs">
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500 dark:text-slate-400">Name</dt>
                  <dd className="text-slate-800 dark:text-slate-100">{doc?.document_name ?? '—'}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500 dark:text-slate-400">Type</dt>
                  <dd className="text-slate-800 dark:text-slate-100">{doc?.document_type ?? '—'}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500 dark:text-slate-400">Case ID</dt>
                  <dd className="text-slate-800 dark:text-slate-100">{doc?.case_id ?? '—'}</dd>
                </div>
              </dl>
            )}
          </div>
          <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4">
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">Sign here</h2>
            <SignaturePad onSubmit={(sig) => submitMu.mutate(sig)} disabled={submitMu.isPending} />
          </div>
        </div>
        <div className="space-y-4">
          <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-4">
            <SignatureAuditList
              documentId={numId}
              signatures={signatures}
              onVerify={(id) => verifyMu.mutate(id)}
              onDownloadSignedPdf={handleDownloadSignedPdf}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentSigningPage;

